import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Checkbox,
    FormControlLabel,
    Button,
    useTheme,
    Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateUsers } from '../../hooks/Users/useCreateUsers';
import { useUpdateUsers } from '../../hooks/Users/useUpdateUsers';
import FormInput from '../inputs/FormInput';
import { useGetRoles } from '../../hooks/roles/useGetRoles';
import LoadingPage from '../global/LoadingPage';
import { useSnackbar } from 'notistack';
import { useUploadMedia } from '../../hooks/media/useUploadMedia';

const FormAddEditUsers = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const { isLoading, roles } = useGetRoles(1, 999);
    const { uploadMedia, isPending: isUploading } = useUploadMedia();
    const { enqueueSnackbar } = useSnackbar();

    const [imageError, setImageError] = useState('');
    const [imageUploadedName, setImageUploadedName] = useState('');

    const { createUsers, isPending: isCreating } = useCreateUsers();
    const { editUsers, isUpdate } = useUpdateUsers();

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        name: '',
        user_name: '',
        email: '',
        phone: '',
        password: '',
        password_confirmation: '',
        is_active: true,
        roles_id: [],
        image: '',
        image_preview: '',
    });

    const mediaId = currentData?.image?.id ?? null;

    useEffect(() => {
        if (!isEdit || !currentData) return;

        setForm((prev) => ({
            ...prev,
            name: currentData.name || '',
            user_name: currentData.user_name || '',
            email: currentData.email || '',
            phone: currentData.phone || '',
            password: '',
            password_confirmation: '',
            is_active: currentData.is_active ?? true,
            roles_id: currentData.roles ? currentData.roles.map((role) => Number(role.id)) : [],
            image: currentData.image?.file_name || '',
            image_preview: currentData.image?.url || currentData.image?.preview || '',
        }));

        setImageUploadedName(currentData.image?.file_name || '');
        setImageError('');
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === 'file') {
            setForm((prev) => ({ ...prev, [name]: files[0] || null }));
            return;
        }

        if (type === 'checkbox') {
            setForm((prev) => ({ ...prev, [name]: checked }));
            return;
        }

        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        setImageError('');
        setImageUploadedName('');

        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        uploadMedia(formData, {
            onSuccess: (res) => {
                const fileName = res?.data?.file_name || res?.data?.name || '';
                if (!fileName) {
                    setImageError(
                        isArabic
                            ? 'تعذر الحصول على اسم الملف من الخادم'
                            : 'Cannot get uploaded file name from server'
                    );
                    return;
                }
                setImageUploadedName(fileName);
                setForm((prev) => ({ ...prev, image: fileName }));
            },
            onError: () => {
                setImageError(
                    isArabic
                        ? 'فشل رفع الصورة، الرجاء المحاولة مرة أخرى'
                        : 'Failed to upload image, please try again'
                );
            },
        });
    };

    const handleRolesChange = (event) => {
        const { value } = event.target;
        setForm((prev) => ({ ...prev, roles_id: value }));
    };

    const isDark = theme.palette.mode === 'dark';

    const handleSubmit = (e) => {
        e.preventDefault();

        if (form.password !== form.password_confirmation) {
            enqueueSnackbar(
                isArabic
                    ? 'كلمة المرور وتأكيدها غير متطابقين'
                    : 'Password and confirmation do not match',
                { variant: 'error' }
            );
            return;
        }

        if (isEdit && currentData?.id) {
            const payload = {
                name: form.name,
                user_name: form.user_name,
                email: form.email,
                phone: form.phone,
                ...(form.password && {
                    password: form.password,
                    password_confirmation: form.password_confirmation,
                }),
                is_active: !!form.is_active,
                roles_id: form.roles_id.map((id) => Number(id)),
                ...(form.image ? { image: form.image } : {}),
            };

            editUsers({ req: payload, id: currentData.id });
        } else {
            const formData = new FormData();
            formData.append('name', form.name);
            formData.append('user_name', form.user_name);
            formData.append('email', form.email);
            formData.append('phone', form.phone);
            formData.append('password', form.password);
            formData.append('password_confirmation', form.password_confirmation);
            formData.append('is_active', form.is_active ? 1 : 0);
            form.roles_id.forEach((id, index) => {
                formData.append(`roles_id[${index}]`, Number(id));
            });

            if (form.image) {
                formData.append('image', form.image);
            }

            createUsers(formData);
        }
    };

    if (isLoading) return <LoadingPage />;

    const rolesOptions = roles ?? [];

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? 'rtl' : 'ltr',
                backgroundColor: isDark && 'transparent',
            }}
        >
            <form onSubmit={handleSubmit}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3,
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <FormInput
                        required={false}
                        label={isArabic ? 'الصورة الشخصية' : 'Avatar'}
                        type="file"
                        name="image"
                        value={form.image_preview}
                        onChange={handleImageChange}
                        labelImage={isArabic ? 'اختر صورة' : 'Choose image'}
                        labelBioImage={
                            isArabic
                                ? 'اسحب وأسقط الصورة هنا أو انقر للاختيار'
                                : 'Drag & drop or click to upload'
                        }
                        isUploading={isUploading}
                        errorMessage={imageError}
                        successMessage={
                            imageUploadedName && !imageError
                                ? isArabic
                                    ? 'تم رفع الصورة بنجاح'
                                    : 'Image uploaded successfully'
                                : ''
                        }
                        sx={{ mx: 'auto' }}
                        mediaId={mediaId}
                        onDeleted={() => {
                            setForm((prev) => ({
                                ...prev,
                                image: '',
                                image_preview: '',
                            }));
                            setImageUploadedName('');
                            setImageError('');
                        }}
                    />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'الاسم' : 'Name'}
                            placeholder={isArabic ? 'ادخل الاسم' : 'Enter name'}
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'اسم المستخدم' : 'Username'}
                            placeholder={isArabic ? 'ادخل اسم المستخدم' : 'Enter username'}
                            type="text"
                            name="user_name"
                            value={form.user_name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'البريد الإلكتروني' : 'Email'}
                            placeholder="admin@example.com"
                            type="text"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'رقم الجوال' : 'Phone'}
                            placeholder="+201000000000"
                            type="text"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'كلمة المرور' : 'Password'}
                            placeholder={isArabic ? 'ادخل كلمة المرور' : 'Enter password'}
                            type="password"
                            name="password"
                            value={form.password}
                            onChange={handleChange}
                            required={!isEdit}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'تأكيد كلمة المرور' : 'Confirm password'}
                            placeholder={
                                isArabic ? 'ادخل تأكيد كلمة المرور' : 'Enter confirm password'
                            }
                            type="password"
                            name="password_confirmation"
                            value={form.password_confirmation}
                            onChange={handleChange}
                            required={!isEdit}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                mt: { xs: 1, md: 3 },
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.is_active}
                                        name="is_active"
                                        onChange={handleChange}
                                        size="small"
                                    />
                                }
                                label={isArabic ? 'مفعّل' : 'Active'}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'الأدوار' : 'Roles'}
                            type="multi_select"
                            name="roles_id"
                            selectedValue={form.roles_id}
                            handleSelectChange={handleRolesChange}
                            options={rolesOptions}
                        />
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                    >
                        {loadingSubmit
                            ? isArabic
                                ? 'جاري الحفظ...'
                                : 'Saving...'
                            : isEdit
                                ? isArabic
                                    ? 'تحديث'
                                    : 'Update'
                                : isArabic
                                    ? 'حفظ'
                                    : 'Save'}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditUsers;
